import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CorrectedValue } from "@/components/agents/configuration-server/CorrectedValue";
import type { RowActionHandlers } from "@/components/agents/configuration-server/OrgStructureListView";
import { OrgUnitRowActions } from "@/components/agents/configuration-server/OrgUnitRowActions";
import {
  hasOrgUnitActions,
  isDispatching,
} from "@/components/agents/configuration-server/orgUnitState";
import { OrgUnitScopeBadge } from "@/components/agents/configuration-server/OrgUnitScopeBadge";
import { OrgUnitStatusBadge } from "@/components/agents/configuration-server/OrgUnitStatusBadge";
import { SUBTYPE_LABEL_BY_VALUE } from "@/lib/org-unit-forms";
import {
  effectiveOrgId,
  extractedValueOf,
  orgUnitTypeLabelKey,
  type OrgAssociation,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";

/** Os mesmos nomes do selo `OrgUnitScopeBadge` — o veredito pendente é
 * "Awaiting approval", para não se confundir com o "Pending" da execução. */
const SCOPE_VERDICT_LABEL: Record<ScopeVerdict, string> = {
  pending: "Awaiting approval",
  approved: "Approved",
  out_of_scope: "Out of scope",
};

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-[#eef1f5] px-6 py-5 first:border-t-0">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#7b8799]">{title}</h3>
      {children}
    </section>
  );
}

function DetailRow({
  label,
  value,
  helper,
  extracted,
  emptyLabel = "—",
}: {
  label: string;
  value: string;
  helper?: string | null;
  /** Valor da extração, quando o consultor corrigiu o campo de uma unidade
   *  extraída (`extractedValueOf`): aparece riscado, com o novo embaixo. */
  extracted?: { value: string | null } | null;
  /** Texto do extraído riscado quando a extração não trouxe valor. */
  emptyLabel?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">{label}</div>
      <div className="text-[13px] font-medium text-[#08254c]">
        {extracted ? (
          <CorrectedValue
            extracted={extracted.value}
            current={value}
            emptyLabel={emptyLabel}
            layout="stacked"
          />
        ) : (
          value
        )}
      </div>
      {helper ? <div className="text-[12px] text-[#7b8799]">{helper}</div> : null}
    </div>
  );
}

function RelatedUnit({
  node,
  emptyLabel,
}: {
  node: {
    id: string;
    targetId?: string | null;
    name: string | null;
    levelName: string;
    unitTypeLabel?: string;
  } | null;
  emptyLabel: string;
}) {
  const { t } = useTranslation();

  if (node == null) {
    return <p className="text-[12px] text-[#7b8799]">{emptyLabel}</p>;
  }

  return (
    <div className="rounded-lg border border-[#e8ebf0] bg-[#fafbfc] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[11px] text-[#08254c]">
          {node.targetId ?? node.id}
        </span>
        <span className="text-[13px] text-[#08254c]">
          {node.name ?? <span className="italic text-[#7b8799]">{t("name not extracted")}</span>}
        </span>
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {t(orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName), {
          defaultValue: node.levelName,
        })}
      </div>
    </div>
  );
}

function AssociatedUnit({ association }: { association: OrgAssociation }) {
  return (
    <div className="rounded-lg border border-[#e8ebf0] bg-[#fafbfc] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[11px] text-[#08254c]">
          {association.id}
        </span>
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {association.level_code}
      </div>
    </div>
  );
}

export function OrgNodeDetailDrawer({
  node,
  onClose,
  actions,
  hideExecution = false,
  hideCrud = false,
}: {
  node: OrgTreeNode | null;
  onClose: () => void;
  // Ações do rodapé. Sem elas o painel fica só de consulta.
  actions?: RowActionHandlers;
  /** Só gerenciamento oculta controles; omitir preserva o drawer existente. */
  hideExecution?: boolean;
  /** Execução (etapa 3): o rodapé não edita nem exclui a unidade. */
  hideCrud?: boolean;
}) {
  const { t } = useTranslation();
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const unitTypeLabel =
    node === null
      ? null
      : t(orgUnitTypeLabelKey(node.unitTypeLabel, node.levelName), {
          defaultValue: node.levelName,
        });

  useEffect(() => {
    if (node !== null && document.activeElement instanceof HTMLElement) {
      restoreFocusRef.current = document.activeElement;
      wasOpenRef.current = true;
      return;
    }

    if (node === null && wasOpenRef.current && restoreFocusRef.current) {
      restoreFocusRef.current.focus();
      wasOpenRef.current = false;
    }
  }, [node]);

  return (
    <Sheet open={node !== null} onOpenChange={(next) => !next && onClose()}>
      {node ? (
        <SheetContent
          side="right"
          // Coluna: cabeçalho fixo no topo, meio com rolagem própria e o
          // rodapé de ações fixo no fundo — com o painel inteiro rolando, o
          // rodapé subia junto quando o conteúdo era curto.
          className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
          onCloseAutoFocus={(event) => {
            if (restoreFocusRef.current) {
              event.preventDefault();
              restoreFocusRef.current.focus();
            }
          }}
        >
          <SheetHeader className="shrink-0 border-b border-[#eef1f5] px-6 py-5">
            <div className="flex flex-wrap items-center gap-2">
              <SheetTitle className="text-left text-[18px] font-semibold text-[#08254c]">
                {t("Organizational unit details")}
              </SheetTitle>
              {/* Antes de passar pelo agente, o escopo (o "Pending" da
                  execução, sozinho, não mudava ao aprovar nem ao tirar do
                  escopo); depois, a execução — "Completed" ao lado de
                  "Awaiting approval" parecia bug. */}
              {node.migrationStatus === "pending" || hideExecution ? (
                <OrgUnitScopeBadge verdict={node.scopeVerdict} />
              ) : (
                <OrgUnitStatusBadge status={node.migrationStatus} />
              )}
            </div>
            <SheetDescription className="text-left text-[13px] text-[#596b85]">
              {t(
                "Technical extraction status, source data, and relations for this organizational unit.",
              )}
            </SheetDescription>
          </SheetHeader>

          <div data-testid="org-node-detail-body" className="min-h-0 flex-1 overflow-y-auto">
            <DetailSection title={t("Identity")}>
              <div className="grid gap-4 sm:grid-cols-3">
                <DetailRow
                  label={t("ID")}
                  value={effectiveOrgId(node)}
                  extracted={extractedValueOf(node, "id")}
                />
                <DetailRow
                  label={t("Name")}
                  value={node.name ?? t("name not extracted")}
                  extracted={extractedValueOf(node, "name")}
                  emptyLabel={t("name not extracted")}
                />
                <DetailRow label={t("Unit type")} value={unitTypeLabel ?? node.levelName} />
              </div>
            </DetailSection>

            <DetailSection title={t("Scope")}>
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailRow label={t("Verdict")} value={t(SCOPE_VERDICT_LABEL[node.scopeVerdict])} />
                <DetailRow label={t("Origin")} value={t(node.origin === "manual" ? "Manual" : "SAP")} />
              </div>
            </DetailSection>

            <DetailSection title={t("Source data")}>
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailRow
                  label={t("Country")}
                  value={node.country ?? t("not extracted")}
                  extracted={extractedValueOf(node, "country")}
                  emptyLabel={t("not extracted")}
                />
                <DetailRow
                  label={t("City")}
                  value={node.city ?? t("city not extracted")}
                  extracted={extractedValueOf(node, "city")}
                  emptyLabel={t("city not extracted")}
                />
                {/* Sem `extracted`: local_currency saiu de `_OVERRIDABLE_FIELDS`
                    (#899) — não é mais gravável pelo consultor, então nunca
                    aparece riscada, mesmo que dado legado ainda tenha o campo
                    em `overriddenFields`. */}
                <DetailRow label={t("Local currency")} value={node.localCurrency ?? t("not available")} />
                {node.subtype ? (
                  <DetailRow
                    label={t("Subtype")}
                    value={t(SUBTYPE_LABEL_BY_VALUE[node.subtype] ?? node.subtype, {
                      defaultValue: SUBTYPE_LABEL_BY_VALUE[node.subtype] ?? node.subtype,
                    })}
                    extracted={extractedValueOf(node, "subtype")}
                    emptyLabel={t("not extracted")}
                  />
                ) : null}
                {node.warehouseNumber ? (
                  <DetailRow label={t("Warehouse number")} value={node.warehouseNumber} />
                ) : null}
              </div>
            </DetailSection>

            <DetailSection title={t("Relations")}>
              <div className="space-y-4">
                {node.relationStatus === "many_to_many" ? (
                  <div className="space-y-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
                      {t("Associated organizational units")}
                    </div>
                    <p className="text-[12px] text-[#7b8799]">
                      {t("This unit belongs to more than one parent in the source system")}
                    </p>
                    {node.associations && node.associations.length > 0 ? (
                      <div className="space-y-2">
                        {node.associations.map((association, index) => (
                          <AssociatedUnit
                            key={`${association.level_code}:${association.id}:${index}`}
                            association={association}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
                      {t("Parent")}
                    </div>
                    <RelatedUnit
                      node={node.parent}
                      emptyLabel={t(
                        node.relationStatus === "not_applicable"
                          ? "This unit is a root in the extracted hierarchy."
                          : "No extracted parent is available for this unit.",
                      )}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
                    {t("Direct children ({{count}})", { count: node.children.length })}
                  </div>
                  {node.children.length === 0 ? (
                    <p className="text-[12px] text-[#7b8799]">{t("This unit has no extracted children.")}</p>
                  ) : (
                    <div className="space-y-2">
                      {node.children.map((child) => (
                        <RelatedUnit key={child.key} node={child} emptyLabel="" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </DetailSection>
          </div>

          {actions && hasOrgUnitActions(node) ? (
            // As mesmas ações da linha/card, pela mesma regra de estado, com o
            // nome por extenso. Fixo no fundo do painel: só o meio rola.
            <div
              data-testid="org-node-detail-actions"
              className="shrink-0 border-t border-[#eef1f5] bg-white px-6 py-4"
            >
              <OrgUnitRowActions
                node={node}
                labeled
                cbcConnected={actions.cbcConnected}
                runInProgressLabel={actions.runInProgressLabel}
                dispatching={isDispatching(actions, node)}
                onRun={hideExecution ? undefined : actions.onRunNode}
                onRequestVerdict={actions.hideVerdict ? undefined : actions.onRequestVerdict}
                hideCrud={hideCrud}
                onEdit={actions.onEditUnit}
                onDelete={actions.onDeleteUnit}
              />
            </div>
          ) : null}
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
